import {
  ActionName,
  ChildConditionType,
  ConditionOperator,
  PropertyConditionName,
} from '@invenco/common-domain/enums';
import { ActionDTO, PropertyConditionDTO, RuleDTO } from '@invenco/common-interface/automation';

/**
 * The editor's render model for rules: what `normalizeRules` builds from
 * stored DTOs and `denormalizeRules` strips back down for save payloads.
 */

/** Which branch of a rule an action belongs to — the `RuleDTO` keys holding action arrays. */
export type EditorActionBranch = {
  [K in keyof RuleDTO]: RuleDTO[K] extends ActionDTO[] ? K : never;
}[keyof RuleDTO];

/** A property condition in the editor's render model; `value` is undefined while incomplete. */
export interface EditorConditionLeaf {
  kind: 'leaf';
  /** Minted at normalize time; editor-only, stripped at denormalize. */
  id: string;
  property: PropertyConditionName;
  operator: ConditionOperator;
  value: PropertyConditionDTO['value'] | undefined;
  subProperty?: string;
  /**
   * Added this session; incomplete styling stays hidden until a failed submit
   * reveals it. Editor-only, stripped at denormalize.
   */
  fresh?: boolean;
}

export interface EditorConditionGroup {
  kind: 'group';
  /** Minted at normalize time; editor-only, stripped at denormalize. */
  id: string;
  /** Static in the UI: root ALL, nested groups ANY. */
  connective: ChildConditionType;
  children: EditorConditionNode[];
}

/**
 * Recursive so stored structures of any depth round-trip faithfully; the UI
 * only edits two levels — deeper data flips `EditorRule.tooDeep`.
 */
export type EditorConditionNode = EditorConditionLeaf | EditorConditionGroup;

export interface EditorRule {
  name: string;
  /** Root is always a group post-normalize. */
  condition: EditorConditionGroup;
  /** The stored condition nests deeper than the UI can edit; the panel shows an alert instead. */
  tooDeep: boolean;
  /**
   * Materialized at normalize so an empty workflow's canvas has a tree;
   * dropped at denormalize while still empty.
   */
  placeholder: boolean;
  actions: EditorAction[];
  elseActions: EditorAction[];
}

export type EditorAction = Omit<ActionDTO, 'type'> & {
  /** Unset until the user picks one — see {@link isConfiguredAction}. */
  type?: ActionName;
  /** See {@link EditorConditionLeaf.fresh}. */
  fresh?: boolean;
};

export type ConfiguredEditorAction = EditorAction & { type: ActionName };

/**
 * A typeless action is unpicked scaffolding: it stages nothing, so it is
 * exempt from validation, invisible to the dirty compare, and dropped at
 * denormalize — abandoning an added card can never block or alter a save.
 */
export const isConfiguredAction = (action: EditorAction): action is ConfiguredEditorAction =>
  action.type !== undefined;
